/**
 * FilterChip.
 *
 * Used in list-page filter bars. A chip that shows an active filter value
 * and can be clicked to clear it. When not active, renders as a subtle
 * button — when active, accent border + clear (X) affordance.
 *
 * Composed with FilterChipGroup to build whole filter rows.
 */
import type { MouseEventHandler, ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface FilterChipProps {
  active?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  onClear?: MouseEventHandler<HTMLButtonElement>;
  children: ReactNode;
  className?: string;
}

export function FilterChip({ active, onClick, onClear, children, className }: FilterChipProps) {
  return (
    <span
      className={cn(
        'inline-flex h-7 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium transition-colors',
        active
          ? 'border-accent bg-accent/5 text-accent'
          : 'border-border-default bg-surface text-secondary hover:border-border-strong hover:text-primary',
        className,
      )}
    >
      <button type="button" onClick={onClick} className="inline-flex items-center gap-1.5">
        {children}
      </button>
      {active && onClear ? (
        <button
          type="button"
          aria-label="Clear filter"
          onClick={onClear}
          className="rounded-sm p-0.5 hover:bg-accent/10"
        >
          <X className="h-3 w-3" strokeWidth={2} />
        </button>
      ) : null}
    </span>
  );
}

export function FilterChipGroup({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn('flex flex-wrap items-center gap-1.5', className)}>{children}</div>;
}
